import React, { ReactElement, useEffect, useState } from "react";
import { CustomModal } from "../CustomModal";
import { useTranslate } from "@obsbot/remo-i18n";
import { Input } from "antd";
import { filterInputValue, filterInputValueForNumber } from "@obsbot/remo-utils";
import { InputWithCode } from "../InputWithCode";
import { withMessage } from "../../HOC/withMessage";
import "./index.less";
import { validateEmail } from "../LoginPage/LoginWithCode/validators";
import { validatePhone } from "../../utils/validators";

export interface BindNewAccountProps {
    visible: boolean;
    isCodeError: boolean;
    setVisible: (flag: boolean) => void;
    setIsCodeError: (flag: boolean) => void;
    onGetCode: (account: string) => Promise<boolean>;
    onConfirm: (account: string, code: string) => void;
}

export const BindNewAccountModal: React.FC<BindNewAccountProps> = withMessage(
    ({
        visible,
        setVisible,
        isCodeError,
        setIsCodeError,
        onGetCode,
        onConfirm,
        message,
    }): ReactElement => {
        const t = useTranslate();
        // 新账号input
        const [account, setAccount] = useState("");
        // 验证码input
        const [code, setCode] = useState("");
        useEffect(() => {
            setAccount("");
            setCode("");
        }, [visible]);
        const handleConfirm = (): void => {
            if (!code && !account) {
                message.warning(
                    t("noInputDouble", {
                        text1: t("bindNewAccount.newAccount"),
                        text2: t("verifyCode"),
                    }),
                );
                return;
            }
            if (!code) {
                message.warning(t("noInputSingle", { text1: t("verifyCode") }));
                return;
            }
            if (!account) {
                message.warning(t("noInputSingle", { text1: t("bindNewAccount.newAccount") }));
                return;
            }
            if (code.length < 6) {
                message.warning(t("identificationVerify.atLeastSix"));
                return;
            }
            onConfirm && onConfirm(code, account);
        };
        return (
            <CustomModal
                className="BindNewAccountModal"
                footer={
                    <div className="modal-button-group-center">
                        <div
                            className="normal-button"
                            onClick={() => {
                                setCode("");
                                setAccount("");
                                setVisible && setVisible(false);
                            }}
                        >
                            {t("cancel")}
                        </div>
                        <div className="group-modal-button-item" onClick={handleConfirm}>
                            {t("done")}
                        </div>
                    </div>
                }
                open={visible}
                renderHeader={t("bindNewAccount.title")}
                setVisible={setVisible}
                onClickClose={() => {
                    setCode("");
                    setAccount("");
                }}
            >
                <div className="bind-new-modal-form">
                    <div className="bind-new-modal-form-item bind-new-modal-account-verify">
                        <div className="modal-form-item-title bind-new-modal-account-title">
                            <span>{t("bindNewAccount.newAccount")}</span>
                        </div>
                        <Input
                            className="custom-modal-form-input"
                            placeholder={t("bindNewAccount.placeholder")}
                            value={account}
                            onChange={e => setAccount(filterInputValue(e.target.value))}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    handleConfirm();
                                }
                            }}
                        />
                    </div>
                    <div className="bind-new-modal-form-item bind-new-modal-newpass">
                        <div className="modal-form-item-title bind-new-modal-account-title">
                            <span>{t("verifyCode")}</span>
                        </div>
                        <InputWithCode
                            className="bind-new-modal-code-input"
                            code={code}
                            errorMsg={t("verifyFailed")}
                            isCodeError={isCodeError}
                            maxLength={6}
                            placeholder={t("verifyCodePlaceholder")}
                            setCode={setCode}
                            t={t}
                            onChange={e => {
                                setCode(filterInputValueForNumber(e.target.value));
                                setIsCodeError && setIsCodeError(false);
                            }}
                            onFocus={() => setIsCodeError && setIsCodeError(false)}
                            onGetCode={() => {
                                let isValidate = true;
                                if (!account) {
                                    isValidate = false;
                                } else if (account.includes("@")) {
                                    isValidate = validateEmail(account);
                                } else {
                                    isValidate = validatePhone(account);
                                }
                                if (!isValidate) {
                                    message.error(t("login-page.enter-account-invalid"));
                                    return Promise.resolve(isValidate);
                                }
                                return onGetCode(account);
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    handleConfirm();
                                }
                            }}
                        />
                    </div>
                </div>
            </CustomModal>
        );
    },
);
